import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';
import axios from 'axios';
import { uniq } from 'lodash';

import { Button, Modal } from '@shared/components/bootstrap';
import { Lightbox } from '@shared/components/helpers/lightbox';
import { useDebounce } from '@shared/hooks';
import { UserRole } from '@shared/types/user_role';

import { AttendancePolicyViolationsList } from '@admin/components/attendance_policy_violations/list';
import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { Dialog } from '@admin/components/helpers/dialog';
import { Map } from '@admin/components/helpers/map';
import { Panel } from '@admin/components/helpers/panel';
import { Roles } from '@admin/components/helpers/roles';
import { Table as HistoriesTable } from '@admin/components/histories/table';
import { AttestationIssuesTable } from '@admin/components/shifts/attestation/attestation_issues_table';
import { ShiftModalContainer } from '@admin/components/shifts/modal_container';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { History__Subject } from '@admin/schema';

import { formatDateTz, ShiftDurationDisplay, ShiftEntryTimeline } from './shift_entry_timeline';
import {
  cleanupFilters,
  dateInputBounds,
  formatFilterDateValue,
  parseDateInput,
  parseFiltersFromSearch,
  restrictFromAndTillFilters,
  syncFiltersToUrl,
} from './shifts_filters';
import { IActionable, IClockin, IShift, IShiftsFilters, ITeam, IUser, ReviewMode, type IImage } from './types';
import { clockinDistanceMiles } from './utilities';

const TIME_CLOCK_PLUS_OPTIONS = [
  { name: 'Enabled', value: 'enabled' },
  { name: 'Disabled', value: 'disabled' },
];

const formatDisplayDate = (date: string) => DateTime.fromISO(date).toFormat('MMM d, yyyy');

const formatShortDate = (timestamp: string) => DateTime.fromISO(timestamp).toFormat('M/d/yy');

export const ShiftsIndex: React.FC = () => {
  useTitle('Shifts');
  const { min: dateMin, max: dateMax } = dateInputBounds();
  const [filters, setFilters] = useState<IShiftsFilters>(() => parseFiltersFromSearch(window.location.search));
  const [mode, setMode] = useState<ReviewMode>((filters.mode as ReviewMode) || 'normal');
  const [teams, setTeams] = useState<ITeam[]>([]);
  const [shifts, setShifts] = useState<IShift[] | undefined>();
  const [actionables, setActionables] = useState<IActionable[]>([]);
  const [selections, setSelections] = useState<IShift[]>([]);
  const [approving, setApproving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingShiftID, setEditingShiftID] = useState<string | undefined>();
  const [createModalDefaults, setCreateModalDefaults] = useState<{ userID: string; shiftDate: string } | undefined>();
  const [historyShift, setHistoryShift] = useState<IShift | undefined>();
  const [lightbox, setLightbox] = useState<IImage | undefined>();
  const [mapClockin, setMapClockin] = useState<IClockin | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { confirm, confirmation } = useConfirmation();
  const debouncedQuery = useDebounce(filters.query);
  const requestIdRef = useRef(0);

  const reviewing = mode === 'review:team' || mode === 'review:all';
  const dates = useMemo(() => uniq(shifts?.map((shift) => shift.date)), [shifts]);

  const loadTeams = useCallback(async () => {
    const { data } = await axios.get<ITeam[]>('/teams.json', { params: { assignable: true } });
    setTeams(data);
  }, []);

  const loadShifts = useCallback(async (activeFilters: IShiftsFilters, activeMode: ReviewMode) => {
    const requestId = ++requestIdRef.current;
    setShifts(undefined);
    setActionables([]);

    const queryFilters = cleanupFilters({ ...activeFilters, mode: activeMode });
    syncFiltersToUrl(activeFilters, activeMode);

    try {
      const [shiftsResponse, actionablesResponse] = await Promise.all([
        axios.get<IShift[]>('/shifts.json', { params: queryFilters }),
        axios.get<IActionable[]>('/shifts/actionables.json', { params: queryFilters }),
      ]);

      if (requestId !== requestIdRef.current) {
        return;
      }

      setShifts(shiftsResponse.data);
      setActionables(actionablesResponse.data);
    } catch {
      if (requestId === requestIdRef.current) {
        setShifts([]);
        setActionables([]);
      }
    }
  }, []);

  const restrictedFilters = useMemo(
    () => restrictFromAndTillFilters(filters),
    [filters.from, filters.till, filters.team_id, filters.time_clock_plus, filters.region_id],
  );

  useEffect(() => {
    if (
      restrictedFilters.from?.getTime() !== filters.from?.getTime() ||
      restrictedFilters.till?.getTime() !== filters.till?.getTime()
    ) {
      setFilters(restrictedFilters);
    }
  }, [restrictedFilters, filters.from, filters.till]);

  useEffect(() => {
    void loadShifts({ ...restrictedFilters, query: debouncedQuery }, mode);
  }, [restrictedFilters, debouncedQuery, mode, loadShifts]);

  useEffect(() => {
    void loadTeams();
  }, [loadTeams]);

  const updateFilters = (next: Partial<IShiftsFilters>, attribute?: 'from' | 'till') => {
    setFilters((current) => restrictFromAndTillFilters({ ...current, ...next }, attribute));
  };

  const toggleMode = (nextMode: ReviewMode) => {
    setSelections([]);
    setMode(nextMode);
  };

  const selectableShifts = useMemo(
    () => shifts?.filter((shift) => shift.started && shift.ended && !shift.approver) ?? [],
    [shifts],
  );

  const hasSelectedShift = (shift: IShift) => selections.some((selected) => selected.id === shift.id);

  const allShiftsSelected = selectableShifts.length > 0 && selectableShifts.length === selections.length;

  const toggleAllSelection = (checked: boolean) => {
    setSelections(checked ? [...selectableShifts] : []);
  };

  const toggleShiftSelection = (shift: IShift, checked: boolean) => {
    setSelections((current) =>
      checked ? [...current, shift] : current.filter((selected) => selected.id !== shift.id),
    );
  };

  const approveSelectedShifts = async () => {
    if (approving || selections.length === 0) {
      return;
    }

    setApproving(true);
    try {
      await axios.post('/shifts/approve.json', { shift_ids: selections.map((shift) => shift.id) });
      toggleMode('normal');
    } catch (error) {
      const message = axios.isAxiosError(error) ? (error.response?.data as { message?: string })?.message : undefined;
      setErrorMessage(message ?? 'An unknown error occurred. Please contact technical support.');
    } finally {
      setApproving(false);
    }
  };

  const shiftsWithoutApprovalForTimeClockPlus = () =>
    shifts?.filter((shift) => !shift.approved_at && !shift.deleted_at && shift.user.time_clock_plus) ?? [];

  const shiftsWithoutTimeClockPlus = () => shifts?.filter((shift) => !shift.user.time_clock_plus) ?? [];

  const exportForTimeClockPlus = async () => {
    if (!shifts) {
      return;
    }

    const unapproved = shiftsWithoutApprovalForTimeClockPlus();
    if (unapproved.length > 0) {
      const names = unapproved.map((shift) => `${shift.user.name} (${shift.date})`).join(' + ');
      setErrorMessage(`The following shifts must be reviewed prior to saving for Time Clock Plus: ${names}`);
      return;
    }

    const withoutTimeClockPlus = shiftsWithoutTimeClockPlus();
    if (withoutTimeClockPlus.length > 0) {
      const names = withoutTimeClockPlus.map((shift) => `${shift.user.name} (${shift.date})`).join(' + ');
      const confirmed = await confirm({
        title: 'Users without Time Clock Plus',
        description: `The following people are disabled for Time Clock Plus and will be skipped: ${names}`,
        confirm: 'Proceed Without Them',
        cancel: 'Cancel',
      });
      if (confirmed !== ConfirmationResponse.Confirm) {
        return;
      }
    }

    const query = new URLSearchParams(
      Object.entries(cleanupFilters({ ...restrictedFilters, query: debouncedQuery, mode })).map(([key, value]) => [
        key,
        String(value),
      ]),
    ).toString();
    window.open(`/shifts/time-clock-plus.csv?${query}`);
  };

  const openEditModal = (shiftID: number) => {
    setEditingShiftID(String(shiftID));
    setShowModal(true);
  };

  const closeShiftModal = () => {
    setShowModal(false);
    setEditingShiftID(undefined);
    setCreateModalDefaults(undefined);
  };

  const openCreateModal = (user?: IUser, shiftDate?: string) => {
    setEditingShiftID(undefined);
    setCreateModalDefaults(user && shiftDate ? { userID: String(user.id), shiftDate } : undefined);
    setShowModal(true);
  };

  const actionablesForDate = (date: string) => actionables.filter((actionable) => actionable.date === date);

  const shiftsForDate = (date: string) => shifts?.filter((shift) => shift.date === date) ?? [];

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      {errorMessage && <Dialog title="Whoops" description={errorMessage} onClose={() => setErrorMessage(undefined)} />}

      {showModal && (
        <ShiftModalContainer
          id={editingShiftID}
          defaultUserID={createModalDefaults?.userID}
          defaultShiftDate={createModalDefaults?.shiftDate}
          onClose={closeShiftModal}
          onChange={() => {
            void loadShifts(filters, mode);
          }}
        />
      )}

      {historyShift && (
        <Modal onClose={() => setHistoryShift(undefined)}>
          <Modal.Content>
            <Modal.Header>
              <Modal.Close close={() => setHistoryShift(undefined)} />
              <Modal.Title>History</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <HistoriesTable
                id={String(historyShift.id)}
                type={History__Subject.Shift}
                timezone={historyShift.user.tz}
              />
            </Modal.Body>
          </Modal.Content>
        </Modal>
      )}

      {lightbox && lightbox && <Lightbox imageURL={lightbox.source} onClose={() => setLightbox(undefined)} />}

      {mapClockin && (
        <Modal onClose={() => setMapClockin(undefined)}>
          <Modal.Content>
            <Modal.Header>
              <Modal.Close close={() => setMapClockin(undefined)} />
            </Modal.Header>
            <Modal.Body>
              <p className="text-center">
                {mapClockin.facility.name} is {clockinDistanceMiles(mapClockin.distance).toFixed(4)} mile(s) away
              </p>
              <Map
                center={{ latitude: mapClockin.latitude, longitude: mapClockin.longitude }}
                marker={{ latitude: mapClockin.latitude, longitude: mapClockin.longitude }}
              />
            </Modal.Body>
          </Modal.Content>
        </Modal>
      )}

      <Panel>
        <Panel.Body>
          <form className="form-inline">
            <div className="form-grouping">
              <label>Team:</label>
              <select
                className="form-control"
                value={filters.team_id ?? ''}
                onChange={(event) =>
                  updateFilters({ team_id: event.target.value ? Number(event.target.value) : undefined })
                }
              >
                <option value=""> - Team - </option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-grouping">
              <label>Time Clock Plus:</label>
              <select
                className="form-control"
                value={filters.time_clock_plus ?? ''}
                onChange={(event) => updateFilters({ time_clock_plus: event.target.value || undefined })}
              >
                <option value=""> - Time Clock Plus - </option>
                {TIME_CLOCK_PLUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-grouping">
              <label>Dates:</label>
              <input
                required
                type="date"
                className="form-control form-control-fit"
                min={dateMin}
                max={dateMax}
                value={formatFilterDateValue(filters.from)}
                onChange={(event) => {
                  const parsed = parseDateInput(event.target.value);
                  if (parsed) {
                    updateFilters({ from: parsed }, 'from');
                  }
                }}
              />
              <span> - </span>
              <input
                required
                type="date"
                className="form-control form-control-fit"
                min={dateMin}
                max={dateMax}
                value={formatFilterDateValue(filters.till)}
                onChange={(event) => {
                  const parsed = parseDateInput(event.target.value);
                  if (parsed) {
                    updateFilters({ till: parsed }, 'till');
                  }
                }}
              />
            </div>
            <div className="form-grouping">
              <label>Search:</label>
              <input
                type="text"
                className="form-control form-control-fit"
                value={filters.query ?? ''}
                onChange={(event) => updateFilters({ query: event.target.value || undefined })}
                placeholder="Search by user name"
              />
            </div>
          </form>
        </Panel.Body>
        <Panel.Footer>
          <div className="pull-right">
            <Button kind="default" disabled={mode === 'review:team'} onClick={() => toggleMode('review:team')}>
              Review Team Hours
            </Button>
            <Roles show={[UserRole.Admin]}>
              {(mode === 'review:team' || mode === 'review:all') && (
                <Button kind="default" disabled={mode === 'review:all'} onClick={() => toggleMode('review:all')}>
                  Review All Hours (Admin Only)
                </Button>
              )}
            </Roles>
            <Button kind="default" onClick={() => openCreateModal()}>
              Add Hours for User
            </Button>
            <Roles show={[UserRole.Admin]}>
              <Button kind="default" disabled={!shifts?.length} onClick={() => void exportForTimeClockPlus()}>
                Export Current View for Time Clock Plus
              </Button>
            </Roles>
          </div>
          <div className="clearfix" />
        </Panel.Footer>
      </Panel>

      <Panel>
        <Panel.Body>
          <AttestationIssuesTable
            filters={{
              from: filters.from,
              till: filters.till,
              mode,
              team_id: filters.team_id ? String(filters.team_id) : undefined,
              time_clock_plus: filters.time_clock_plus,
              query: filters.query,
            }}
          />
        </Panel.Body>
      </Panel>

      <AttendancePolicyViolationsList
        from={filters.from ? (DateTime.fromJSDate(filters.from).toISODate() ?? undefined) : undefined}
        teamID={filters.team_id ? String(filters.team_id) : undefined}
        teamReviewMode={mode === 'review:team'}
        till={filters.till ? (DateTime.fromJSDate(filters.till).toISODate() ?? undefined) : undefined}
        timeClockPlusUsersOnly={filters.time_clock_plus === 'enabled'}
        userName={filters.query}
      />

      <Panel>
        <Panel.Body>
          {reviewing && (
            <label>
              <input
                type="checkbox"
                checked={allShiftsSelected}
                onChange={(event) => toggleAllSelection(event.target.checked)}
              />{' '}
              Select All
            </label>
          )}
          <div className="table-responsive">
            <h3>Shifts</h3>
            <table className="table shifts-table">
              <thead>
                <tr>
                  {reviewing && <th className="col-zero">Approve?</th>}
                  <th className="col-sm-2 text-left">Name</th>
                  <th className="col-sm-8" />
                  <th className="col-sm-2 text-right">Hours</th>
                  <th className="col-zero text-right" />
                </tr>
              </thead>
              <tbody>
                {dates.map((date) => (
                  <React.Fragment key={date}>
                    <tr>
                      <th colSpan={reviewing ? 5 : 4}>{formatDisplayDate(date)}</th>
                    </tr>
                    {actionablesForDate(date).map((actionable) => (
                      <tr key={`actionable-${actionable.user.id}-${date}`}>
                        {reviewing && (
                          <td className="col-zero">
                            <input type="checkbox" disabled />
                          </td>
                        )}
                        <td className="col-sm-2 text-left text-danger">{actionable.user.name}</td>
                        <td className="col-sm-8 text-center text-danger">
                          <div className="shifts">
                            <span className="shifts-entry-warn shifts-entry-fallback-message">
                              No Shift Entered (dispatched:{' '}
                              {formatDateTz(actionable.dispatch.arrival, actionable.dispatch.region.tz, 'h:mm a')})
                            </span>
                          </div>
                        </td>
                        <td />
                        <td className="col-zero text-right">
                          <button
                            type="button"
                            className="btn btn-mint"
                            onClick={() => openCreateModal(actionable.user, actionable.date)}
                          >
                            Add
                          </button>
                        </td>
                      </tr>
                    ))}
                    {shiftsForDate(date).map((shift) => (
                      <tr key={shift.id}>
                        {reviewing && (
                          <td className="col-zero">
                            <input
                              type="checkbox"
                              id={`shifts_${shift.id}_selected`}
                              name={`shifts[${shift.id}][selected]`}
                              checked={!!shift.approver || hasSelectedShift(shift)}
                              disabled={!!shift.approver || !shift.started || !shift.ended}
                              onChange={(event) => toggleShiftSelection(shift, event.target.checked)}
                            />
                          </td>
                        )}
                        <td className="col-sm-2 text-left">
                          <div>{shift.user.name}</div>
                          {shift.deleter && (
                            <div className="shifts-entry-deleted shifts-entry-tiny">
                              deleted by {shift.deleter.name} on {formatShortDate(shift.deleted_at!)}
                            </div>
                          )}
                          {shift.approver && (
                            <div className="shifts-entry-approved shifts-entry-tiny">
                              approved by {shift.approver.name} on {formatShortDate(shift.approved_at!)}
                            </div>
                          )}
                          {shift.violations && (
                            <ul className="text-danger shifts-violations-list">
                              {shift.violations.map((violation, index) => (
                                <li key={index} className="shifts-entry-warn shifts-entry-tiny">
                                  {violation.message}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="col-sm-8">
                          <ShiftEntryTimeline
                            shift={shift}
                            onLightbox={(image) => {
                              if (!image) return;
                              if (image.state !== 'uploaded') return;
                              setLightbox(image);
                            }}
                            onMap={setMapClockin}
                          />
                        </td>
                        <td className="col-sm-2 text-left">
                          {shift.overridden_at && (
                            <button type="button" className="shifts-link" onClick={() => setHistoryShift(shift)}>
                              View Changes
                            </button>
                          )}
                          <span className="pull-right">
                            <ShiftDurationDisplay duration={shift.duration} />
                          </span>
                          <div className="clearfix" />
                        </td>
                        <td className="col-zero col-nowrap">
                          <Button
                            kind="default"
                            disabled={!!shift.hrs_synced_at}
                            title={
                              shift.hrs_synced_at
                                ? 'This shift has already been synced to Kronos and cannot be edited'
                                : undefined
                            }
                            onClick={() => openEditModal(shift.id)}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {shifts && shifts.length === 0 && <div className="text-center">No Shifts Found Matching Criteria</div>}
          {!shifts && <Spinner />}
        </Panel.Body>
        {reviewing && (
          <Panel.Footer>
            <div className="pull-right">
              <Button kind="default" disabled={approving} onClick={() => toggleMode('normal')}>
                Cancel
              </Button>
              <Button kind="primary" disabled={approving} onClick={() => void approveSelectedShifts()}>
                Approve
              </Button>
            </div>
            <div className="clearfix" />
          </Panel.Footer>
        )}
      </Panel>
    </>
  );
};
